import { err, ok, Result } from 'neverthrow';
import { Ty } from '../ty';
import { CompileOutput, ParseCtx, ParseError, ParseRes } from './eraql-ast/ast';
import { WithMeta, WithRange } from './lex/types';
import { TokenStream } from './lex';
import { buildEraQLAst } from './eraql-ast/build-eraql-ast';
import { compileAst, exprOfItem } from './eraql-ast/compile';
import _ from 'lodash';
import { Expression, Markup, Relation } from '../builder';

const { capitalize } = _;

export type Res<T> = Result<
  T,
  readonly WithRange<ParseError & { readonly original: string }>[]
>;

export const parseTy = (
  ty: string | Ty.ExtendedAttributeType,
  ctx?: ParseCtx
): Res<Ty.ExtendedAttributeType> => {
  if (typeof ty === 'string') {
    return parse(ty, ctx).andThen((res): Res<Ty.ExtendedAttributeType> => {
      switch (res.item.t) {
        case 'ty':
          return ok(res.item.ty);
        default:
          return err([
            [
              { t: 'expected', expected: 'type', original: ty },
              { range: [0, ty.length] },
            ] as const,
          ]);
      }
    });
  } else {
    return ok(ty);
  }
};

export function formatParseError(error: ParseError): {
  readonly t: string;
  readonly msg: string;
} {
  let message: string;

  switch (error.t) {
    case 'cant-tag':
    case 'cant-pipe-into':
    case 'cant-call':
    case 'invalid-arity':
    case 'invalid-enum-variant':
    case 'unexpected-eof':
    case 'unexpected-token':
      message = `${capitalize(error.t.replace(/-/g, ' '))}.`;
      break;

    case 'expected': {
      const expectedStr = Array.isArray(error.expected)
        ? error.expected.join(', ')
        : error.expected;
      message = `Expected ${expectedStr}.`;
      break;
    }
    case 'attribute-not-found':
      message = `Attribute not found: "${error.name}".`;
      break;

    case 'unclosed-deliminator':
      message = `Unclosed delimiter, missing: "${error.missing}".`;
      break;

    case 'duplicate-key':
    case 'missing-key':
    case 'unexpected-key':
      message = `${error.t.replace(/-/g, ' ')} for key: "${error.key}".`;
      break;

    case 'type-check-error':
      message = `${error.trace.headline}- ${error.trace.message}`;
      break;

    default:
      message = 'Unknown error type.';
      break;
  }

  return { t: error.t, msg: message };
}

export const parseMu = (str: string, ctx?: ParseCtx): Res<Markup> =>
  parse(str, ctx).andThen(({ item }) => {
    switch (item.t) {
      case 'mu':
        return ok(item.mu);
      case 'expr':
        return ok(
          Markup.fromAst({ t: 'text', text: item.expr.ast, modifier: 'none' })
        );
      default:
        return err([
          [
            { t: 'expected', expected: 'markup', original: str },
            { range: [0, str.length] },
          ] as const,
        ]);
    }
  });

export const parseExpr = (str: string, ctx?: ParseCtx): Res<Expression> =>
  parse(str, ctx).andThen(
    (res): Res<Expression> =>
      exprOfItem(
        [
          res.item,
          { range: [0, str.length], comments: { leading: [], trailing: null } },
        ],
        ctx ?? {}
      ).mapErr((es) => es.map(([e, meta]) => [{ ...e, original: str }, meta]))
  );

export const parseRel = (str: string, ctx?: ParseCtx): Res<Relation> =>
  parse(str, ctx).andThen((res): Res<Relation> => {
    switch (res.item.t) {
      case 'rel':
        return ok(res.item.rel);
      default:
        return err([
          [
            { t: 'expected', expected: 'rel', original: str },
            { range: [0, str.length] },
          ] as const,
        ]);
    }
  });

export const parse = (str: string, ctx?: ParseCtx): Res<CompileOutput> => {
  const lex = new TokenStream(str, { offset: ctx?.offset });
  const result: Res<CompileOutput> = buildEraQLAst(lex, 0)
    .andThen(
      (eraAst): ParseRes<CompileOutput> =>
        eraAst !== null
          ? compileAst(eraAst, ctx ?? {})
          : err([[{ t: 'unexpected-eof' }, { range: [0, 0] }]])
    )
    .mapErr((errs): WithRange<ParseError & { original: string }>[] =>
      errs.map(([err, meta]) => [
        { ...err, original: lex.originalForMeta(meta) },
        meta,
      ])
    );

  if (result.isErr()) {
    return err(result.error);
  }

  const next = lex.consume();
  if (next !== null) {
    const e: WithMeta<ParseError & { original: string }> = [
      {
        t: 'unexpected-token',
        original: lex.originalForMeta(next[1]),
      },
      next[1],
    ];
    return err([e]);
  }

  return result;
};
